import React, { useState, useEffect } from 'react';
import axios from 'axios';
import usePagination from '../../../utils/usePagination';
import Pagination from '../../../components/common/Pagination';

const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmt = (n) => `Rs ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const formatArea = (kanals, marlas) => {
  const k = parseInt(kanals || 0);
  const m = parseInt(marlas || 0);
  if (k === 0 && m === 0) return '—';
  if (m === 0) return `${k} kanal`;
  if (k === 0) return `${m} marla`;
  return `${k}k ${m}m`;
};

const ProfitabilityTab = () => {
  const [data, setData] = useState({ summary: null, crops: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('per_kanal_desc');

  const fetchData = (statusFilter = '') => {
    const params = statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {};
    axios.get(`${API}/agriculture/profitability`, { headers: headers(), params })
      .then(r => {
        setData(r.data.data || { summary: null, crops: [] });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchData(filter); }, [filter]);

  // Sort crops by chosen criteria
  const sortedCrops = [...(data.crops || [])].sort((a, b) => {
    switch (sortBy) {
      case 'per_kanal_desc': return b.profit.profit_per_kanal - a.profit.profit_per_kanal;
      case 'per_kanal_asc':  return a.profit.profit_per_kanal - b.profit.profit_per_kanal;
      case 'profit_desc':    return b.profit.net_profit - a.profit.net_profit;
      case 'profit_asc':     return a.profit.net_profit - b.profit.net_profit;
      case 'revenue_desc':   return b.revenue.total - a.revenue.total;
      default:               return 0;
    }
  });

  const { paginatedData, currentPage, setCurrentPage, totalPages } = usePagination(sortedCrops, 10);

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>Loading profitability report...</div>;

  const summary = data.summary || {
    total_revenue: 0, total_expenses: 0, total_net_profit: 0,
    profitable_count: 0, loss_count: 0, crop_count: 0,
    total_kanals: 0, average_profit_per_kanal: 0
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>Crop Profitability Analysis</h2>
        <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
          📊 Per-crop & per-kanal profit/loss breakdown
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '4px solid #3498db' }}>
          <div style={{ fontSize: '24px' }}>📦</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#3498db' }}>{summary.crop_count}</div>
          <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Total Crops</div>
        </div>
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '4px solid #1abc9c' }}>
          <div style={{ fontSize: '24px' }}>💰</div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#1abc9c' }}>{fmt(summary.total_revenue)}</div>
          <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Total Revenue</div>
        </div>
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '4px solid #e74c3c' }}>
          <div style={{ fontSize: '24px' }}>📉</div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#e74c3c' }}>{fmt(summary.total_expenses)}</div>
          <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Total Expenses</div>
        </div>
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: `4px solid ${summary.total_net_profit >= 0 ? '#27ae60' : '#e74c3c'}` }}>
          <div style={{ fontSize: '24px' }}>{summary.total_net_profit >= 0 ? '📈' : '⚠️'}</div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: summary.total_net_profit >= 0 ? '#27ae60' : '#e74c3c' }}>{fmt(summary.total_net_profit)}</div>
          <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Net Profit/Loss</div>
        </div>
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '4px solid #27ae60' }}>
          <div style={{ fontSize: '24px' }}>✅</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#27ae60' }}>{summary.profitable_count}</div>
          <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Profitable</div>
        </div>
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '4px solid #e74c3c' }}>
          <div style={{ fontSize: '24px' }}>❌</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#e74c3c' }}>{summary.loss_count}</div>
          <div style={{ fontSize: '13px', color: '#7f8c8d' }}>In Loss</div>
        </div>
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: `4px solid ${summary.average_profit_per_kanal >= 0 ? '#9b59b6' : '#c0392b'}` }}>
          <div style={{ fontSize: '24px' }}>📐</div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: summary.average_profit_per_kanal >= 0 ? '#9b59b6' : '#c0392b' }}>
            {fmt(summary.average_profit_per_kanal)}
          </div>
          <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Avg / Kanal</div>
        </div>
      </div>

      {/* Filter & Sort Controls */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {['all', 'growing', 'harvested', 'sold', 'failed'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer',
              fontWeight: '600', fontSize: '13px',
              background: filter === f ? '#27ae60' : '#f0f0f0',
              color: filter === f ? 'white' : '#555'
            }}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: '#7f8c8d', fontWeight: '600' }}>Sort by:</span>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            style={{ padding: '8px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}
          >
            <option value="per_kanal_desc">Per-Kanal Profit (High → Low)</option>
            <option value="per_kanal_asc">Per-Kanal Profit (Low → High)</option>
            <option value="profit_desc">Net Profit (High → Low)</option>
            <option value="profit_asc">Net Profit (Low → High)</option>
            <option value="revenue_desc">Revenue (High → Low)</option>
          </select>
        </div>
      </div>

      {/* Profitability Table */}
      <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {['Crop', 'Field', 'Area', 'Revenue', 'Lease', 'Labor', 'Inventory', 'Other', 'Total Exp', 'Net P/L', 'Per Kanal'].map(h => (
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedCrops.length === 0 ? (
              <tr><td colSpan={11} style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>No crops to analyze. Add some crops with sales/expenses to see profitability.</td></tr>
            ) : paginatedData.map((r, i) => {
              const isProfit = r.profit.net_profit >= 0;
              const isPerKanalProfit = r.profit.profit_per_kanal >= 0;
              return (
                <tr key={r.crop.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                  <td style={{ padding: '12px 14px', fontWeight: '700', color: '#2c3e50' }}>
                    {r.crop.crop_name}
                    {r.crop.crop_type && <div style={{ fontSize: '11px', color: '#999', fontWeight: '400' }}>{r.crop.crop_type}</div>}
                  </td>
                  <td style={{ padding: '12px 14px', color: '#555' }}>{r.crop.field_name || '—'}</td>
                  <td style={{ padding: '12px 14px', color: '#555', fontWeight: '600' }}>
                    {formatArea(r.crop.area_sown_kanals, r.crop.area_sown_marlas)}
                  </td>
                  <td style={{ padding: '12px 14px', color: '#1abc9c', fontWeight: '600' }}>{fmt(r.revenue.total)}</td>
                  <td style={{ padding: '12px 14px', color: '#555' }}>{fmt(r.expenses.lease_share)}</td>
                  <td style={{ padding: '12px 14px', color: '#555' }}>{fmt(r.expenses.labor_cost)}</td>
                  <td style={{ padding: '12px 14px', color: '#555' }}>{fmt(r.expenses.inventory_cost)}</td>
                  <td style={{ padding: '12px 14px', color: '#555' }}>{fmt(r.expenses.other_expenses)}</td>
                  <td style={{ padding: '12px 14px', color: '#e74c3c', fontWeight: '600' }}>{fmt(r.expenses.total)}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{
                      padding: '4px 10px', borderRadius: '6px', fontWeight: '700',
                      background: isProfit ? '#27ae6020' : '#e74c3c20',
                      color: isProfit ? '#27ae60' : '#e74c3c'
                    }}>
                      {isProfit ? '+' : ''}{fmt(r.profit.net_profit)}
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{
                      padding: '4px 10px', borderRadius: '6px', fontWeight: '700',
                      background: isPerKanalProfit ? '#27ae6020' : '#e74c3c20',
                      color: isPerKanalProfit ? '#27ae60' : '#e74c3c'
                    }}>
                      {isPerKanalProfit ? '+' : ''}{fmt(r.profit.profit_per_kanal)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalRecords={sortedCrops.length}
          rowsPerPage={10}
        />
      </div>

      {/* Helper note */}
      <div style={{ marginTop: '20px', padding: '16px 20px', background: '#fff3cd', borderRadius: '10px', borderLeft: '4px solid #f39c12', fontSize: '13px', color: '#7f5b00' }}>
        💡 <strong>Tip:</strong> The "Per Kanal" column tells you which crop gives the most profit per kanal of land. Sort by this to identify your most efficient crops for next season's planning.
      </div>
    </div>
  );
};

export default ProfitabilityTab;